import dayjs from "dayjs";
import { parseAsArrayOf, parseAsString, parseAsStringEnum } from "nuqs";
import { format } from "~/common/utils/ApiHelper";
import { SHORTCUT_DEFINITIONS, type ShortcutKey } from "~/common/components/AdvancedDateRangePicker/shortcuts";
import { QUERY_TYPE, type QueryType } from "~/features/orders/common/queryTypes";
import {
  type OrderListSearchBoxData,
  type OrderListSearchFormData,
} from "~/features/orders/orderList/components/OrderListSearchBox/OrderListSearchBox";
import { QUOTE_SEARCH_QUERY_KEYS } from "~/domains/orders/quoteSearchUrlState";

const QUOTE_QUERY_TYPES = Object.values(QUERY_TYPE) as QueryType[];
const QUOTE_DATE_SHORTCUTS = [...SHORTCUT_DEFINITIONS.map(({ key }) => key), "custom"] as ShortcutKey[];

export const QUOTE_SEARCH_QUERY_PARAMS = {
  [QUOTE_SEARCH_QUERY_KEYS.queryType]: parseAsStringEnum(QUOTE_QUERY_TYPES).withDefault(QUERY_TYPE.PURCHASE_NO),
  [QUOTE_SEARCH_QUERY_KEYS.dateShortcut]: parseAsStringEnum(QUOTE_DATE_SHORTCUTS).withDefault("last30Days"),
  [QUOTE_SEARCH_QUERY_KEYS.dateFrom]: parseAsString,
  [QUOTE_SEARCH_QUERY_KEYS.dateTo]: parseAsString,
  [QUOTE_SEARCH_QUERY_KEYS.status]: parseAsString.withDefault(""),
  [QUOTE_SEARCH_QUERY_KEYS.lineStatus]: parseAsArrayOf(parseAsString).withDefault([]),
};

export const QUOTE_SEARCH_QUERY_OPTIONS = {
  history: "replace" as const,
  clearOnDefault: false,
};

export interface QuoteSearchQueryState {
  quoteQueryType: QueryType;
  quoteDateShortcut: ShortcutKey;
  quoteDateFrom: string | null;
  quoteDateTo: string | null;
  quoteStatus: string;
  quoteLineStatus: string[];
}

export interface NormalizedQuoteSearchQuery extends Omit<QuoteSearchQueryState, "quoteDateFrom" | "quoteDateTo"> {
  quoteDateFrom: string;
  quoteDateTo: string;
}

export function normalizeQuoteSearchQuery(query: QuoteSearchQueryState): NormalizedQuoteSearchQuery {
  const defaults = getDefaultDateValues();
  let quoteDateFrom = normalizeDate(query.quoteDateFrom, defaults.quoteDateFrom);
  let quoteDateTo = normalizeDate(query.quoteDateTo, defaults.quoteDateTo);

  if (dayjs(quoteDateFrom).isAfter(dayjs(quoteDateTo), "day")) {
    quoteDateFrom = defaults.quoteDateFrom;
    quoteDateTo = defaults.quoteDateTo;
  }

  return {
    quoteQueryType: query.quoteQueryType,
    quoteDateShortcut: query.quoteDateShortcut ?? (query.quoteDateFrom || query.quoteDateTo ? "custom" : "last30Days"),
    quoteDateFrom,
    quoteDateTo,
    quoteStatus: query.quoteStatus ?? "",
    quoteLineStatus: [...(query.quoteLineStatus ?? [])].filter(Boolean),
  };
}

export function quoteSearchDataToQuery(data: OrderListSearchBoxData): QuoteSearchQueryState {
  return {
    quoteQueryType: data.queryType,
    quoteDateShortcut: data.dateShortcut ?? "custom",
    quoteDateFrom: data.dateFrom ? format(data.dateFrom) : null,
    quoteDateTo: data.dateTo ? format(data.dateTo) : null,
    quoteStatus: data.status ?? "",
    quoteLineStatus: data.lineStatus ?? [],
  };
}

export function quoteSearchQueryToFormData(
  query: NormalizedQuoteSearchQuery,
): Pick<OrderListSearchFormData, "dateRange" | "searchBy" | "statusOption" | "lineStatusOption"> {
  return {
    dateRange: {
      shortcut: query.quoteDateShortcut,
      dates: [dayjs(query.quoteDateFrom), dayjs(query.quoteDateTo)],
    },
    searchBy: {
      // Query values are private business identifiers and remain ephemeral to the current tab.
      query: "",
      type: query.quoteQueryType,
    },
    statusOption: query.quoteStatus,
    lineStatusOption: query.quoteLineStatus,
  };
}

function getDefaultDateValues(): Pick<NormalizedQuoteSearchQuery, "quoteDateFrom" | "quoteDateTo"> {
  return {
    quoteDateFrom: format(dayjs().subtract(30, "day").toDate()),
    quoteDateTo: format(dayjs().toDate()),
  };
}

function normalizeDate(value: string | null, fallback: string): string {
  if (!value) {
    return fallback;
  }

  const parsed = dayjs(value);
  return parsed.isValid() && parsed.format("YYYY-MM-DD") === value ? value : fallback;
}
